import React from 'react';
import Card from '../ui/Card';
import { UsersIcon, CheckCircleIcon, ClockIcon, XCircleIcon, AlertTriangleIcon } from 'lucide-react';
import { DashboardFilters } from '../../pages/Dashboard';
const baseStats = [{
  label: 'Total Registered',
  baseValue: 5432,
  icon: UsersIcon,
  color: 'text-primary-500',
  bg: 'bg-primary-50 dark:bg-primary-900/20',
  border: 'border-primary-200 dark:border-primary-800'
}, {
  label: 'Active',
  baseValue: 4890,
  icon: CheckCircleIcon,
  color: 'text-emerald-500',
  bg: 'bg-emerald-50 dark:bg-emerald-900/20',
  border: 'border-emerald-200 dark:border-emerald-800'
}, {
  label: 'Pending Approvals',
  baseValue: 124,
  icon: ClockIcon,
  color: 'text-amber-500',
  bg: 'bg-amber-50 dark:bg-amber-900/20',
  border: 'border-amber-200 dark:border-amber-800'
}, {
  label: 'Rejected',
  baseValue: 18,
  icon: XCircleIcon,
  color: 'text-rose-500',
  bg: 'bg-rose-50 dark:bg-rose-900/20',
  border: 'border-rose-200 dark:border-rose-800'
}, {
  label: 'Suspended',
  baseValue: 35,
  icon: AlertTriangleIcon,
  color: 'text-orange-500',
  bg: 'bg-orange-50 dark:bg-orange-900/20',
  border: 'border-orange-200 dark:border-orange-800'
}];
const timeMultipliers = {
  Today: 1,
  Week: 1.02,
  MTD: 1.08,
  YTD: 1.45
};
const baseWorkflowData = {
  makerSubmission: 47,
  checkerApproval: 32
};
interface MerchantStatsProps {
  filters: DashboardFilters;
}
export const MerchantStats: React.FC<MerchantStatsProps> = ({
  filters
}) => {
  const {
    timeRange
  } = filters;
  const timeMult = timeMultipliers[timeRange];
  const stats = baseStats.map(stat => ({
    ...stat,
    value: Math.round(stat.baseValue * timeMult).toLocaleString()
  }));
  const workflowData = {
    makerSubmission: Math.round(baseWorkflowData.makerSubmission * timeMult),
    checkerApproval: Math.round(baseWorkflowData.checkerApproval * timeMult)
  };
  return <Card title="Merchant Operations" className="h-full flex flex-col" data-id="element-175">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 mb-8" data-id="element-176">
        {stats.map((stat, index) => {
        const Icon = stat.icon;
        return <div key={index} className={`flex flex-col items-center justify-center p-4 bg-white dark:bg-slate-800/50 rounded-xl border ${stat.border} shadow-sm hover:shadow-md transition-shadow relative overflow-hidden`} data-id="element-177">
              <div className={`absolute top-0 left-0 w-full h-1 ${stat.bg.split(' ')[0]}`} data-id="element-178"></div>
              <div className={`p-2 rounded-lg ${stat.bg} mb-3`} data-id="element-179">
                <Icon className={`h-5 w-5 ${stat.color}`} strokeWidth={2.5} data-id="element-180" />
              </div>
              <span className="text-2xl font-bold text-slate-900 dark:text-white tracking-tight" data-id="element-181">
                {stat.value}
              </span>
              <span className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 dark:text-slate-400 text-center mt-1.5" data-id="element-182">
                {stat.label}
              </span>
            </div>;
      })}
      </div>

      <div className="mt-auto" data-id="element-183">
        <h4 className="text-sm font-semibold text-slate-900 dark:text-white mb-4 flex items-center" data-id="element-184">
          <div className="w-2 h-2 rounded-full bg-accent-500 mr-2" data-id="element-185"></div>
          Maker / Checker Workflow
        </h4>
        <div className="overflow-hidden rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm" data-id="element-186">
          <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-700" data-id="element-187">
            <thead className="bg-slate-50 dark:bg-slate-800/80" data-id="element-188">
              <tr data-id="element-189">
                <th scope="col" className="px-5 py-3 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider" data-id="element-190">
                  Metric
                </th>
                <th scope="col" className="px-5 py-3 text-right text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider" data-id="element-191">
                  Count
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-slate-800 divide-y divide-slate-100 dark:divide-slate-700/50" data-id="element-192">
              <tr className="hover:bg-slate-50 dark:hover:bg-slate-700/30 transition-colors" data-id="element-193">
                <td className="px-5 py-4 text-sm font-medium text-slate-700 dark:text-slate-300 flex items-center" data-id="element-194">
                  <div className="w-1.5 h-1.5 rounded-full bg-amber-500 mr-3" data-id="element-195"></div>
                  Merchants Awaiting Maker Submission
                </td>
                <td className="px-5 py-4 text-sm font-bold text-slate-900 dark:text-white text-right" data-id="element-196">
                  {workflowData.makerSubmission}
                </td>
              </tr>
              <tr className="hover:bg-slate-50 dark:hover:bg-slate-700/30 transition-colors" data-id="element-197">
                <td className="px-5 py-4 text-sm font-medium text-slate-700 dark:text-slate-300 flex items-center" data-id="element-198">
                  <div className="w-1.5 h-1.5 rounded-full bg-accent-500 mr-3" data-id="element-199"></div>
                  Merchants Awaiting Checker Approval
                </td>
                <td className="px-5 py-4 text-sm font-bold text-slate-900 dark:text-white text-right" data-id="element-200">
                  {workflowData.checkerApproval}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </Card>;
};